import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-review";
import { useMemo } from "react";
import { useRxData } from "rxdb-hooks";
import {
  CREDENTIALING_THRESHOLDS,
  type CredStatus,
  computeCredStatus,
} from "../../_lib/credentialing-config";
import { useCan } from "../../_lib/rbac/use-can";
import { MATCH_NOTHING } from "../../_lib/rxdb-selectors";

// ─── Types ────────────────────────────────────────────────────────────────────

export type PerformerRow = {
  performerId: string;
  /** Total submitted exam count for this performer */
  totalExams: number;
  /** Avg qualityScore formatted to 1 d.p., or "—" when no reviews */
  avgScore: string;
  /** Submitted eFAST exams */
  efastCount: number;
  /** Submitted Cardiac exams */
  cardiacCount: number;
  /** Submitted Lung exams */
  lungCount: number;
  /** Overall credentialing status — credentialed only when all program exam types are met */
  credStatus: CredStatus;
};

export type ProgramStats = {
  /** Distinct non-null performerIds across all encounters */
  activePerformers: number;
  /** Submitted exams whose parent encounter date falls within the current calendar month */
  examsThisMonth: number;
  /** Avg qualityScore across all reviews, "—" if none */
  avgQaScore: string;
  /** "X / Y" fully-credentialed performers / total */
  credentialedFraction: string;
  /** One row per distinct performer, sorted by totalExams descending */
  performerRows: PerformerRow[];
};

// ─── Constants ────────────────────────────────────────────────────────────────

const PROGRAM_EXAM_TYPES = [
  "efast",
  "cardiac",
  "lung",
] as const satisfies readonly ExamType[];
type ProgramExamType = (typeof PROGRAM_EXAM_TYPES)[number];

const isProgramExamType = (t: ExamType): t is ProgramExamType =>
  (PROGRAM_EXAM_TYPES as readonly ExamType[]).includes(t);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getMonthStart = (): number => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
};

const formatScore = (sum: number, count: number): string =>
  count > 0 ? (sum / count).toFixed(1) : "—";

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useProgramStats = (): ProgramStats => {
  // Program-wide reads are gated here, not just at the route boundary: these
  // collections hold every performer's data, so a guard on the page alone would
  // leave peer records readable from the hook by any role (PNE-530).
  const canViewProgram = useCan("viewProgramDashboard");

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) =>
      canViewProgram
        ? collection?.find()
        : collection?.find({ selector: MATCH_NOTHING }),
  );

  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      canViewProgram
        ? collection?.find()
        : collection?.find({ selector: MATCH_NOTHING }),
  );

  const { result: reviews } = useRxData<EpocusReview>(
    RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
    (collection) =>
      canViewProgram
        ? collection?.find()
        : collection?.find({ selector: MATCH_NOTHING }),
  );

  return useMemo(() => {
    const monthStart = getMonthStart();

    // ── Summary stats ────────────────────────────────────────────────────────

    const performerIds = new Set<string>();
    for (const enc of encounters) {
      if (enc.performerId != null) performerIds.add(enc.performerId);
    }

    const monthEncounterIds = new Set(
      encounters
        .filter((e) => new Date(e.date).getTime() >= monthStart)
        .map((e) => e.id),
    );
    const examsThisMonth = exams.filter(
      (e) => e.status === "submitted" && monthEncounterIds.has(e.encounterId),
    ).length;

    const avgQaScore = formatScore(
      reviews.reduce((sum, r) => sum + r.qualityScore, 0),
      reviews.length,
    );

    // ── Build lookup maps ─────────────────────────────────────────────────────

    // performerId → encounter IDs
    const encounterIdsByPerformer = new Map<string, string[]>();
    for (const enc of encounters) {
      if (enc.performerId == null) continue;
      const list = encounterIdsByPerformer.get(enc.performerId);
      if (list) {
        list.push(enc.id);
      } else {
        encounterIdsByPerformer.set(enc.performerId, [enc.id]);
      }
    }

    // encounterId → submitted exams
    const submittedExamsByEncounterId = new Map<string, EpocusExam[]>();
    for (const exam of exams) {
      if (exam.status !== "submitted") continue;
      const list = submittedExamsByEncounterId.get(exam.encounterId);
      if (list) {
        list.push(exam);
      } else {
        submittedExamsByEncounterId.set(exam.encounterId, [exam]);
      }
    }

    // examId → reviews
    const reviewsByExamId = new Map<string, EpocusReview[]>();
    for (const review of reviews) {
      const list = reviewsByExamId.get(review.examId);
      if (list) {
        list.push(review);
      } else {
        reviewsByExamId.set(review.examId, [review]);
      }
    }

    // ── Per-performer rows ────────────────────────────────────────────────────

    const performerRows: PerformerRow[] = [...performerIds]
      .map((pid) => {
        const encIds = encounterIdsByPerformer.get(pid) ?? [];

        // Collect all submitted exams for this performer
        const allExams: EpocusExam[] = [];
        for (const encId of encIds) {
          const examList = submittedExamsByEncounterId.get(encId);
          if (examList) allExams.push(...examList);
        }

        const totalExams = allExams.length;

        // Count by program exam types, and accumulate per-type review scores
        const typeCounts: Record<ProgramExamType, number> = {
          efast: 0,
          cardiac: 0,
          lung: 0,
        };
        const typeScoreSums: Record<ProgramExamType, number> = {
          efast: 0,
          cardiac: 0,
          lung: 0,
        };
        const typeScoreCounts: Record<ProgramExamType, number> = {
          efast: 0,
          cardiac: 0,
          lung: 0,
        };

        // Collect all reviews for this performer's exams (overall + per-type)
        let scoreSum = 0;
        let scoreCount = 0;

        for (const exam of allExams) {
          const examReviews = reviewsByExamId.get(exam.id) ?? [];
          for (const r of examReviews) {
            scoreSum += r.qualityScore;
            scoreCount += 1;
          }

          if (isProgramExamType(exam.examType)) {
            typeCounts[exam.examType] += 1;
            for (const r of examReviews) {
              typeScoreSums[exam.examType] += r.qualityScore;
              typeScoreCounts[exam.examType] += 1;
            }
          }
        }

        // Per-type average score (null when no reviews for that type)
        const typeAvgScore = (t: ProgramExamType): number | null =>
          typeScoreCounts[t] > 0 ? typeScoreSums[t] / typeScoreCounts[t] : null;

        // A performer is fully credentialed only when all program types are met
        const typeStatuses = PROGRAM_EXAM_TYPES.map((t) =>
          computeCredStatus(
            typeCounts[t],
            typeAvgScore(t),
            CREDENTIALING_THRESHOLDS[t],
          ),
        );
        const credStatus: CredStatus = typeStatuses.every(
          (s) => s === "credentialed",
        )
          ? "credentialed"
          : typeStatuses.some((s) => s !== "not_started")
            ? "in_progress"
            : "not_started";

        return {
          performerId: pid,
          totalExams,
          avgScore: formatScore(scoreSum, scoreCount),
          efastCount: typeCounts.efast,
          cardiacCount: typeCounts.cardiac,
          lungCount: typeCounts.lung,
          credStatus,
        };
      })
      .sort((a, b) => b.totalExams - a.totalExams);

    // ── Credentialed fraction ─────────────────────────────────────────────────

    const credentialedCount = performerRows.filter(
      (r) => r.credStatus === "credentialed",
    ).length;
    const credentialedFraction = `${credentialedCount} / ${performerRows.length}`;

    return {
      activePerformers: performerIds.size,
      examsThisMonth,
      avgQaScore,
      credentialedFraction,
      performerRows,
    };
  }, [encounters, exams, reviews]);
};
